import { Modal, Form, Input, Upload, message, Image } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';

const MAX_TOTAL_ATTACHMENT_SIZE = 32 * 1024 * 1024; // 32MB combined across all reports

const isImageFile = (file) => /\.(jpe?g|png)$/i.test(file.name || '') || /^image\//.test(file.type || '');

const ReferralReportModal = ({ open, onClose, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const totalSizeRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    totalSizeRef.current = 0;
  }, [open, form]);

  const beforeUpload = (file) => {
    if (totalSizeRef.current + file.size > MAX_TOTAL_ATTACHMENT_SIZE) {
      message.error('Combined report size cannot exceed 32MB');
      return Upload.LIST_IGNORE;
    }
    totalSizeRef.current += file.size;
    const thumbUrl = isImageFile(file) ? URL.createObjectURL(file) : undefined;
    setFileList((prev) => [...prev, {
      uid: file.uid, name: file.name, status: 'done', originFileObj: file, url: thumbUrl, thumbUrl,
    }]);
    return Upload.LIST_IGNORE;
  };

  const handleRemove = (file) => {
    if (file.thumbUrl?.startsWith('blob:')) { URL.revokeObjectURL(file.thumbUrl); }
    totalSizeRef.current = Math.max(0, totalSizeRef.current - (file.originFileObj?.size || 0));
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handlePreview = (file) => {
    if (isImageFile(file)) {
      setPreviewImage(file.url || file.thumbUrl);
      setPreviewOpen(true);
    } else {
      window.open(file.url || file.thumbUrl, '_blank', 'noopener');
    }
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSubmit({
        ...values,
        files: fileList.map((f) => f.originFileObj),
      });
    });
  };

  return (
    <Modal
      title="Upload Report"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      width={560}
      okText="Submit Report"
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="diagnosisSummary"
          label="Diagnosis Summary"
          rules={[{ required: true, message: 'Please provide a diagnosis summary' }]}
        >
          <Input.TextArea rows={3} placeholder="Summarize the diagnosis…" maxLength={2000} />
        </Form.Item>
        <Form.Item
          name="treatmentSummary"
          label="Treatment Summary"
          rules={[{ required: true, message: 'Please provide a treatment summary' }]}
        >
          <Input.TextArea rows={3} placeholder="Summarize the treatment provided…" maxLength={2000} />
        </Form.Item>
        <Form.Item
          label="Diagnosis / Treatment Reports"
          extra="PDF, JPG or PNG · Select multiple files at once · Click an image to preview · Combined size up to 32MB (optional)"
        >
          <Upload.Dragger
            multiple
            fileList={fileList}
            beforeUpload={beforeUpload}
            onRemove={handleRemove}
            onPreview={handlePreview}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            accept="application/pdf,image/jpeg,image/jpg,image/png"
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag files to this area to upload</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>

      <Image
        style={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
        src={previewImage}
      />
    </Modal>
  );
};

export default ReferralReportModal;
